import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  Component,
  OnInit,
  ViewEncapsulation,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  AfterViewInit,
  ElementRef,
  TemplateRef,
  Output,
  EventEmitter,
  AfterViewChecked,
  OnDestroy,
  Renderer2,
  signal,
  inject,
  ExperimentalPendingTasks,
  effect
} from '@angular/core';
import { trigger, transition, style, animate, query, stagger } from '@angular/animations';
import { Observable, BehaviorSubject, Subscription, Subject, of, fromEvent, timeout, EMPTY } from 'rxjs';
import { filter, map, tap, debounceTime, delay, timeoutWith, catchError} from 'rxjs/operators';
import { NgScrollbar, NgScrollbarExt, AsyncDetection, ScrollViewport } from 'ngx-scrollbar';
import { ScrollBarService } from '@shared/scroll/scrollbar.service';
import { Platform } from '@angular/cdk/platform';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { QueryRef } from 'apollo-angular';
import { VirtualScrollerComponent, IPageInfo } from '@shared/components/ngx-virtual-scroller';
import { IPage } from '@collections/common/interfaces/page.interface';
import { FromServerService } from '@shared/services/from-server/from-server.service';
import { NgTemplateOutlet, NgClass, AsyncPipe } from '@angular/common';
import { VirtualScrollerComponent as VirtualScrollerComponent_1 } from '../ngx-virtual-scroller/index';

export class PageScrollerInfo {
  currentPageNumber = 0;
  pageSize = 0;
  totalItems = 0;
  totalPages = 0;
  startIndex = 0;
  endIndex = 0;

  constructor(_currentPageNumber: number, _pageSize: number, _totalItems: number) {
    this.currentPageNumber = _currentPageNumber;
    this.pageSize = _pageSize;
    this.totalItems = _totalItems;
    this.totalPages = Math.ceil(_totalItems / _pageSize);
  }

  get isEOF(): boolean {
    return this.currentPageNumber === this.totalPages;
  }

  get isBOF(): boolean {
    return this.currentPageNumber === 1;
  }
}
/**
 * dunque, sarebbe opportuno creare un virtual scroller per questa pagina, per la vista a blocchi, ma, al momento,
 * il virtual scroll di Material, che vorrei adottare al posto dell'ngx-virtual-scroll,
 * non supporta ancora il multi-column.
 * La feauture è nella road map, ma non c'è una data di rilascio.
 * https://github.com/angular/components/issues/10114
 * Per il momento lascio così. Vediamo gli sviluppi.
 */
@UntilDestroy()
@Component({
    selector: 'paged-virtual-scroller',
    templateUrl: './paged-virtual-scroller.component.html',
    styleUrls: ['./paged-virtual-scroller.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('listStagger', [
            transition('* => scaleUp', [
                query('.scrollable-content > .ct > *', [
                    style({ opacity: 0, transform: 'scale(0.7)' }),
                    stagger('70ms', animate('0.7s cubic-bezier(.8, -0.6, 0.2, 1.5)', style({ opacity: 1, transform: 'scale(1)' })))
                ], { optional: true })
            ])
        ])
    ],
    standalone: true,
    imports: [NgScrollbarExt, AsyncDetection, VirtualScrollerComponent_1, ScrollViewport, NgTemplateOutlet, NgClass, RouterLink, AsyncPipe]
})
export class PagedVirtualScrollerComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {

  @ViewChild(VirtualScrollerComponent, { static: false }) vs: VirtualScrollerComponent;
  @ViewChild('spacer', { static: false }) spacer: ElementRef;
  @ViewChild(NgScrollbar, { static: false }) scrollBar: NgScrollbar;
  mainScrollBar: NgScrollbar;

  @Input()
  get itemsQuery(): QueryRef<any> {
    return this._itemsQuery;
  }
  set itemsQuery(v: QueryRef<any>) {
    this._itemsQuery = v;
    this._remaining = [];
    if (!this.query$) {
      this.query$ = new BehaviorSubject<QueryRef<any>>(v);
    } else {
      this.query$.next(v);
    }

  }
  private _itemsQuery: QueryRef<any>;

  get visibleItems(): number {
    if (!this._visibleItems) {
      this._visibleItems = this.getVisibleItems();
    }
    return this._visibleItems;
  }
  set visibleItems(v: number) {
    this._visibleItems = v;
  }
  private _visibleItems: number;

  // non c'è ancora la possibilità di acccedere alle opzioni all'interno della queryRef
  // per cui, al momento, è necessario fornire il parametro a parte.
  // https://github.com/kamilkisiela/apollo-angular/issues/1324
  @Input() pageSize = 10;
  @Input() maxPending = 10;
  @Input() itemTpl: TemplateRef<any>;
  @Input() scrollWithMainScrollbar = false;
  @Input() horizontal = false;
  @Input() scaleUpOnRender = false;
  @Input() enableVirtualScroll = true;
  @Input() wrapInScrollbar = false;
  @Input() seoFriendly: 'random-cursor' | 'offset-cursor';
  @Input() seoFriendlyMaxDepth: number = 20;
  @Input() itemWidth: number;
  @Input() itemHeight: number;
  @Input() parentScroll: HTMLElement;
  @Input() renderOnInVisible = false;
  @Input() renderOnTimeOut = -1;
  @Input() enableUnequalChildrenSizes = false;
  @Input() emptyTpl: TemplateRef<any>;

  @Output() pageChanged: EventEmitter<PageScrollerInfo> = new EventEmitter();

  seoFriendlyHasNextPage: boolean;
  items = signal<any[]>(null);
  items$: Observable<any[]>;
  itemSubscription: Subscription;

  isRendered = false;

  afterRender$: Subject<boolean>;
  enterAnim: string;
  page: IPage<any>;
  currentPageInfo: PageScrollerInfo;
  spacerHeight: number;

  isEmpty: boolean = false;

  private isLoading = signal(false);
  private needFetchMore = false;
  protected data: any[] = [];
  private pendingItems = [];
  private currentPageSize: number;
  private query$: BehaviorSubject<QueryRef<any>>;
  private onVsUpdateSubscription: Subscription | null;
  private _remaining = [];
  private afterCursor: string;

  private readonly SEO_FRIENDLY_PAGE_SIZE = 20;

  offsetCursor: number;

  renderSeoLink = signal(false);

  getRenderSeoLink(): boolean {
    const { offsetCursor, SEO_FRIENDLY_PAGE_SIZE, page } = this;
    if (this.seoFriendly === 'offset-cursor') {
      return (page && (offsetCursor < page?.totalCount))/*  &&
        (offsetCursor / this.SEO_FRIENDLY_PAGE_SIZE) < this.seoFriendlyMaxDepth; 
        rimosso il limite a 20 pagine per la SEO*/
    } else {
      return page?.pageInfo.hasNextPage;
    }

  }

  private _pageChangePending = false;

  pendingTasks;
  taskDone = () => {};

  constructor(
    public cd: ChangeDetectorRef,
    private scrollBarService: ScrollBarService,
    public elementRef: ElementRef,
    private fromServerService: FromServerService,
    public platform: Platform,
    private activatedRoute: ActivatedRoute,
    private renderer: Renderer2
  ) {
    this.afterRender$ = new Subject();
    this.afterCursor = '';
    if(!this.platform.isBrowser) {
      this.pendingTasks = inject(ExperimentalPendingTasks);
      this.taskDone = this.pendingTasks.add();
    }
  }

  ngOnInit(): void {
    if (this.platform.isBrowser) {
      if (this.scrollWithMainScrollbar) {
        this.parentScroll = this.scrollBarService.mainScrollbar?.viewport.nativeElement;
      }

      this.afterRender$
      .pipe(
        delay(10),
        untilDestroyed(this)
      )
      .subscribe(() => this.fetchMore(this.vs.viewPortInfo)); // esegue il fetchMore, se necessario, dopo il render.

      if (this.enableVirtualScroll) {
        // first render
        this.onVsUpdate();
      }
    } 

    if (this.seoFriendly) {
      this.activatedRoute.queryParams
      .subscribe(params => {
        this.afterCursor = params['after'];
        const offset = parseInt(this.afterCursor, 10);
        this.offsetCursor = !isNaN(offset) ? offset + this.SEO_FRIENDLY_PAGE_SIZE : this.SEO_FRIENDLY_PAGE_SIZE;
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.platform.isBrowser && this.enableVirtualScroll) {
      fromEvent(window, 'resize')
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(() => this.visibleItems = 0);
    }

    this.query$
    .pipe(
      untilDestroyed(this)
    )
    .subscribe((res) => {
      this.scrollBar?.scrollTo({ top: 0, duration: 0 });
      this.attachDataSource();
    });
  }

  ngAfterViewChecked(): void {
    if (this.platform.isBrowser && this.enableVirtualScroll && !this.onVsUpdateSubscription) {
      this.onVsUpdate();
    }
  }

  ngOnDestroy(): void {
    this.data = [];
    this.onVsUpdateSubscription = null;
  }

  private onVsUpdate(): void {
    if (!this.vs) {
      return;
    }

    this.onVsUpdateSubscription = this.vs.vsUpdate
    .pipe(
      tap((data: any[]) => {
        if (data.length === 0) {
          this.pageChanged.emit(new PageScrollerInfo(0, 0, 0));
        }
      }),
      filter((data: any[]) => data.length > 0),
      this.scaleUpOnRender ? debounceTime(100)  : debounceTime(0), // Da sistemare. Così non è l'ideale. Bisogna riprogettare con signal.
      untilDestroyed(this)
    ).subscribe(data => {
      const startIndex = this.vs.viewPortInfo.startIndex;
      const endIndex = this.vs.viewPortInfo.endIndex;
      if (this.currentPageInfo) {
        this.currentPageInfo.startIndex = startIndex;
        this.currentPageInfo.endIndex = endIndex;
        this.currentPageInfo.totalItems = this.page.totalCount;
        this.pageChanged.emit(this.currentPageInfo);
      }
      if (this._pageChangePending) {
        this.vs.viewPortItems = this.data.slice(startIndex, endIndex + 2);
        this._pageChangePending = false;
        this.cd.detectChanges();
        this.scrollToIndex(this.vs.viewPortInfo.endIndex + 1, 'forward');
      }
      if (!this.isRendered) {
        this.isRendered = true;
        // esegue animazione solo se la pagina non viene caricata direttamente dal server
        if (!this.fromServerService.isFromServer && this.scaleUpOnRender) {
          this.enterAnim = 'scaleUp';
        } else {
          this.afterRender$.next(true);
        }
        this.cd.detectChanges();
      }
    });
  }

  private attachDataSource(): void {
    if (this.itemSubscription) {
      this.itemSubscription.unsubscribe();
    }
    this.isRendered = false;
    this.enterAnim = '';

    if (!this.platform.isBrowser && this.seoFriendly && this.afterCursor) {
      this.itemsQuery.variables.limit = this.SEO_FRIENDLY_PAGE_SIZE;
      this.itemsQuery.variables.after = this.afterCursor;
    }

    this.spacerHeight = 0;

      this.items$ = this.itemsQuery
      .valueChanges
      .pipe(
        filter((res) => !res.loading || res.data),
        map(res => res.data ? res.data[Object.keys(res.data)[0]] : null),
        tap((page: IPage<any>) => {
          if(page) {
            this.page = page;
            if (page.edges.length < (this.pageSize + 1)) { //first page
              // this._remaining = [];
              this.spacerHeight = 0;
            }
            const pageNumber = this.currentPageInfo?.currentPageNumber > 0 ? this.currentPageInfo.currentPageNumber : 1;
            this.currentPageInfo = new PageScrollerInfo(pageNumber, this.pageSize, this.page.totalCount);
            this.currentPageSize = page.edges.length;
            this.renderSeoLink()
            this.pageChanged.emit(this.currentPageInfo);
          } else {
            throw(new Error('No data found'));
          }
        }),
        map((page) => page?.edges.map(edge => edge.node)),
        catchError((err) => {
          if(!this.platform.isBrowser) {
            this.taskDone();
          }
          console.log(err)
          return EMPTY;
        })
      );

    this.itemSubscription = this.items$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(items => {
        this.isEmpty = items.length === 0;
        this.data = items;
        this.pendingItems.splice(0, this.currentPageSize);
        // this._remaining?.splice(0, this.currentPageSize);
        if (this.pendingItems.length < this.maxPending) {
          this.isLoading.set(false);
        } else {
          this.isLoading.set(true);
        }
        this.items.set(items.concat(this._remaining));
        this.renderSeoLink.set(this.getRenderSeoLink());
        if(!this.platform.isBrowser) {
          this.taskDone();
        }
      });
  }

  trackByFn(index, item): string {
    if (!item?._id)
      return index; 
    return item._id;
  }

  updateContainer(scrollLength: any): void {
    if (this.scrollWithMainScrollbar) {
      this.renderer.setStyle(this.spacer.nativeElement, 'height', `${scrollLength}px`);
    }
  }

  fetchMore(ev: IPageInfo): void {
    if (this.pageSize === 1 || !this.isRendered || this.pendingItems.length > this.maxPending) {
      return;
    }

    if (ev.endIndex >= this.data.length + this.pendingItems.length - 1 && this.page?.pageInfo.hasNextPage || this.needFetchMore) {
      let pageSize = this.pageSize;
      // const remaining = this.page.totalCount - this.data.length - this.pendingItems.length;

      // if (remaining > 0) {
      //   pageSize = pageSize > remaining ? remaining : pageSize;
      //   this.pendingItems = this.pendingItems.concat(Array.from({ length: pageSize }));
      //   if (this._remaining.length === 0) {
      //     this._remaining = Array.from({ length: this.page.totalCount - this.data.length});
      //     this.items.set(this.data.concat(this._remaining));
      //   }

      // }

      if (this.isLoading()) {
        this.needFetchMore = true;
        return;
      }
      this.needFetchMore = false;
      if(!this.page.pageInfo.hasNextPage) {
        return;
      }
      this.isLoading.set(true);
      
      this.currentPageSize = Math.max(this.pageSize, this.pendingItems.length);
      this.itemsQuery.fetchMore({
        variables: {
          first: this.currentPageSize,
          after: this.page.pageInfo.endCursor
        }
      });
    }
  }

  getVisibleItems(): number {
    const selector = '.scrollable-content';
    const {itemsPerRow, itemsPerCol} = this.getContainedItems(selector);

    return itemsPerRow ? itemsPerRow * itemsPerCol : 0;
  }

  private getContainedItems(viewportSelector: string): { itemsPerRow: number, itemsPerCol: number, rowHeight: number } {
    const viewport = this.elementRef.nativeElement.querySelector(viewportSelector);
    const itemsCt = this.scrollWithMainScrollbar ? viewport.children : viewport.firstChild?.children;
    if (itemsCt?.length > 0) {
      const h = viewport.offsetHeight;
      const w = viewport.offsetWidth;
      const item =  itemsCt[0];
      if (item) {
        if (item instanceof HTMLElement) {
          const viewportStyles = getComputedStyle(viewport);
          const itemStyles = getComputedStyle(item);

          const horizontalMargin = parseInt(viewportStyles['margin-left'], 10) || 0 +
          parseInt(viewportStyles['margin-right'], 10) || 0 + parseInt(viewportStyles['column-gap'], 10) || 0;
          const visibleItemsPerRow = Math.floor((w + horizontalMargin) / (item.offsetWidth + horizontalMargin));
          const vertcalMargin = parseInt(itemStyles['margin-bottom'], 10) || 0 + parseInt(itemStyles['margin-top'], 10) || 0 + parseInt(viewportStyles['row-gap'], 10) || 0;
          const rowHeight = item.offsetHeight + vertcalMargin;
          const visibleItemsPerCol = h > 0 ? Math.floor((h + vertcalMargin) / rowHeight): 0;

          return { itemsPerRow: visibleItemsPerRow, itemsPerCol: visibleItemsPerCol, rowHeight: rowHeight };
        } else {
          throw new Error('PVS: Uncompatible item found!');
        }
      }
    }

    return { itemsPerRow: 0, itemsPerCol: 0, rowHeight: 0 };
  }

  previousPage(): void {
    const p = this.currentPageInfo;
    this.scrollToIndex((p.currentPageNumber - 2) * this.visibleItems, 'back');
  }

  nextPage(): void {
    const p = this.currentPageInfo;
    if (this.pageSize === 1 && this.vs.viewPortInfo.endIndex === (this.data.length + this.pendingItems.length - 1)) {
      this._pageChangePending = true;

      this.itemsQuery.fetchMore({
        variables: {
          first: 1,
          after: this.page.pageInfo.endCursor
        }
      });
    } else {
      const index = Math.min((p.currentPageNumber * this.visibleItems), (this.page.totalCount - 1));
      this.scrollToIndex(index, 'forward');
    }

  }

  scrollToIndex(index: number, dir: 'back' | 'forward'): void {
    this.vs?.scrollToIndex(index, true, 0, 500, () => {
      this.currentPageInfo.currentPageNumber += dir === 'forward' ? +1 : -1;
      this.pageChanged.emit(this.currentPageInfo);
      this.cd.markForCheck();
    });
  }
  refreshVsMeasurements(ev): void {
    if (ev.toState === 'scaleUp') {
      this.vs.invalidateAllCachedMeasurements();
      this.afterRender$.next(true);
    }
  }

  updateScrollbar(sb: NgScrollbarExt): void {
    if(sb.viewport.initialized()) {
      sb.update();
    }
  }
}
